// What the user has chosen so far while creating a task group (docs/12 "Task groups as built"):
// task type → workflow → source and its filter → a binding per credential requirement → name.
// Pure, like the apps' TaskGroupDraft, so the rules are tested without a page.
import type { Schemas } from "../../api/client";
import { parseRequirements } from "../../catalog/parse";
import type { CredentialRequirement, Requirement, Toolchain } from "../../catalog/types";
import { requiredToolchains } from "../../workflows/requirements/model";
import type { Source } from "../model";
import { EMPTY_QUERY, type JqlQuery, toJql } from "./jql";
import { cadenceMinutes, type Cadence } from "./cadence";
import { repositoriesProblem, type RepositoryRow, wireRepositories } from "./repositories";

export type MarketplaceWorkflow = Schemas["MarketplaceWorkflowDto"];
export type Credential = Schemas["CredentialDto"];

/** The API's limit on tasks of one group running at once. */
export const MAX_PARALLEL = 32;

export const STEPS = ["type", "workflow", "source", "access", "review"] as const;
export type StepKey = (typeof STEPS)[number];

export interface Draft {
  taskTypeId: string;
  workflow: MarketplaceWorkflow | null;
  source: Source;
  /** The credential the poller lists the source with; "" for none. */
  credentialId: string;
  github: { repo: string; labels: string; assignee: string; includeClosed: boolean };
  /** `includeSubtasks` also lists the open sub-tasks of matching issues (S127); not part of the JQL. */
  jira: { raw: boolean; query: JqlQuery; text: string; includeSubtasks: boolean };
  asana: { project: string; section: string };
  cadence: Cadence;
  repositories: RepositoryRow[];
  /** Requirement index → credential id or "platform". */
  bindings: Record<number, string>;
  name: string;
  maxParallel: number;
  /** Steps wait while the environment's VPN is down (D80). */
  requiresVpn: boolean;
  /** The network list the agents' containers use (D81); "" for the platform defaults. */
  networkListId: string;
  /** Let the containers reach what the VM's network (its VPN included) reaches. */
  shareVmNetwork: boolean;
  /** Toolchains for the group's own worker image (D90), set right after the group is created. */
  toolchains: Toolchain[];
}

export const EMPTY_DRAFT: Draft = {
  taskTypeId: "",
  workflow: null,
  source: "manual",
  credentialId: "",
  github: { repo: "", labels: "", assignee: "", includeClosed: false },
  jira: { raw: false, query: EMPTY_QUERY, text: "", includeSubtasks: false },
  asana: { project: "", section: "" },
  cadence: { preset: 5, custom: "" },
  repositories: [],
  bindings: {},
  name: "",
  maxParallel: 1,
  requiresVpn: false,
  networkListId: "",
  shareVmNetwork: false,
  toolchains: [],
};

/** The credential kinds that can list each polled source. */
export const SOURCE_KINDS: Record<Exclude<Source, "manual">, string[]> = {
  github: ["github_pat", "github_app"],
  jira: ["jira_token"],
  asana: ["asana_token"],
};

export const sourceKinds = (source: Source): string[] => (source === "manual" ? [] : SOURCE_KINDS[source]);

/** Whether a credential of `kind` meets the requirement; `anthropic` is either Anthropic kind. */
export function accepts(r: CredentialRequirement, kind: string): boolean {
  if (r.credential_kind === "anthropic") return kind === "anthropic_api_key" || kind === "anthropic_oauth_token";
  return r.credential_kind === kind;
}

/** The kind a new secret for this requirement would have (a subscription token for `anthropic`). */
export const kindFor = (r: CredentialRequirement): string => (r.credential_kind === "anthropic" ? "anthropic_oauth_token" : r.credential_kind);

export const requirementsOf = (workflow: MarketplaceWorkflow | null): Requirement[] =>
  workflow ? parseRequirements(workflow.version.requirements) : [];

export interface IndexedCredential {
  index: number;
  requirement: CredentialRequirement;
}

export function credentialRequirements(workflow: MarketplaceWorkflow | null): IndexedCredential[] {
  return requirementsOf(workflow).flatMap((r, index) => (r.kind === "credential" ? [{ index, requirement: r }] : []));
}

export const jqlOf = (d: Draft): string => (d.jira.raw ? d.jira.text.trim() : toJql(d.jira.query));

const listItems = (text: string): string[] => text.split(",").map((s) => s.trim()).filter((s) => s !== "");

/** The `source_config` the API takes for the chosen source. */
export function sourceConfig(d: Draft): Record<string, unknown> {
  switch (d.source) {
    case "manual":
      return {};
    case "github": {
      const labels = listItems(d.github.labels);
      const assignee = d.github.assignee.trim();
      return { repo: d.github.repo.trim(), ...(labels.length ? { labels } : {}), ...(assignee ? { assignee } : {}), state: d.github.includeClosed ? "all" : "open" };
    }
    case "jira":
      return { jql: jqlOf(d), ...(d.jira.includeSubtasks ? { include_subtasks: true } : {}) };
    case "asana": {
      const section = d.asana.section.trim();
      return { project: d.asana.project.trim(), ...(section ? { section } : {}) };
    }
  }
}

export type SourceProblem = "repo" | "project" | "jql" | "asanaProject" | "credential" | "cadence" | "repositories";

const REPO = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;

/** Why the source step cannot continue: the filter, the credential, the cadence, the repositories. */
export function sourceProblem(d: Draft): SourceProblem | null {
  if (d.source === "github" && !REPO.test(d.github.repo.trim())) return "repo";
  if (d.source === "jira" && !d.jira.raw && d.jira.query.projects.length === 0) return "project";
  if (d.source === "jira" && (jqlOf(d) === "" || jqlOf(d).length > 2000)) return "jql";
  if (d.source === "asana" && !/^\d+$/.test(d.asana.project.trim())) return "asanaProject";
  if (d.source !== "manual" && d.credentialId === "") return "credential";
  if (d.source !== "manual" && cadenceMinutes(d.cadence) === null) return "cadence";
  return repositoriesProblem(d.repositories, d.source === "github" ? d.github.repo.trim() : "") ? "repositories" : null;
}

/** Bind the obvious: the source's credential where it fits, else the only match, else the platform key. */
export function prefill(d: Draft, credentials: Credential[]): Draft {
  const bindings = { ...d.bindings };
  const sourceCredential = credentials.find((c) => c.id === d.credentialId);
  for (const { index, requirement } of credentialRequirements(d.workflow)) {
    if (bindings[index] !== undefined) continue;
    const matches = credentials.filter((c) => accepts(requirement, c.kind));
    if (sourceCredential && accepts(requirement, sourceCredential.kind)) bindings[index] = sourceCredential.id;
    else if (matches.length === 1) bindings[index] = matches[0]!.id;
    else if (matches.length === 0 && requirement.platform_default) bindings[index] = "platform";
  }
  const toolchains = [...d.toolchains, ...requiredToolchainsOf(d.workflow).filter((t) => !d.toolchains.includes(t))];
  return { ...d, bindings, toolchains };
}

/** The toolchains the workflow requires (D90). */
export const requiredToolchainsOf = (workflow: MarketplaceWorkflow | null): Toolchain[] => requiredToolchains(requirementsOf(workflow));

/** Required toolchains not chosen. */
export const missingToolchains = (d: Draft): Toolchain[] => requiredToolchainsOf(d.workflow).filter((t) => !d.toolchains.includes(t));

/** Required credentials still unbound (a `platform_default` one falls back to the platform key). */
export const missingBindings = (d: Draft): IndexedCredential[] =>
  credentialRequirements(d.workflow).filter(({ index, requirement }) => requirement.required && !requirement.platform_default && !d.bindings[index]);

/** Where tasks come from, in a few words, for the default name. */
function sourceWords(d: Draft): string {
  if (d.source === "github") return d.github.repo.trim();
  if (d.source === "jira") return d.jira.raw ? "Jira" : d.jira.query.projects.join(", ") || "Jira";
  if (d.source === "asana") return "Asana";
  return "";
}

export function suggestedName(d: Draft): string {
  const words = sourceWords(d);
  const base = d.workflow?.name ?? "";
  return words ? `${base} · ${words}` : base;
}

export function stepReady(d: Draft, step: StepKey): boolean {
  switch (step) {
    case "type":
      return d.taskTypeId !== "";
    case "workflow":
      return d.workflow !== null && d.workflow.task_type_id === d.taskTypeId;
    case "source":
      return sourceProblem(d) === null;
    case "access":
      return missingBindings(d).length === 0 && missingToolchains(d).length === 0;
    case "review":
      return d.name.trim() !== "" && Number.isInteger(d.maxParallel) && d.maxParallel >= 1 && d.maxParallel <= MAX_PARALLEL;
  }
}

/** The first step not yet ready: as far as the user may go. */
export function reachable(d: Draft): number {
  const at = STEPS.findIndex((s) => !stepReady(d, s));
  return at < 0 ? STEPS.length - 1 : at;
}

/** `POST /api/task-groups`'s body. */
export function createBody(d: Draft) {
  const polled = d.source !== "manual";
  return {
    name: d.name.trim(),
    workflow_version_id: d.workflow?.version.id ?? "",
    source: d.source,
    source_config: sourceConfig(d),
    ...(polled ? { source_credential_id: d.credentialId, poll_minutes: cadenceMinutes(d.cadence) ?? 5 } : {}),
    max_parallel_tasks: d.maxParallel,
    requires_vpn: d.requiresVpn,
    ...networkBody(d),
    bindings: Object.fromEntries(Object.entries(d.bindings).filter(([, v]) => v !== "")),
    repositories: wireRepositories(d.repositories),
  };
}

/** The D81 settings both bodies carry. */
export const networkBody = (d: Draft) => ({ network_list_id: d.networkListId || null, share_vm_network: d.shareVmNetwork });
